import { useState, useEffect } from 'react'
import { PageHeader } from '@/components/layout'
import {
  Card,
  CardContent,
  Button,
  Badge,
  EmptyState,
  Skeleton,
  Alert,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  Label,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui'
import { Key, Copy, Trash2, Plus, RefreshCw, Check, AlertCircle } from 'lucide-react'
import { useApiClient, api, type AuthorizedApp, type AuthorizedAppDetail, type APIKey, type APIKeyCreated } from '@/api'

export function ApiKeysPage() {
  const client = useApiClient()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [apps, setApps] = useState<AuthorizedApp[]>([])
  const [selectedAppSlug, setSelectedAppSlug] = useState<string>('')
  const [selectedAppDetail, setSelectedAppDetail] = useState<AuthorizedAppDetail | null>(null)
  const [keys, setKeys] = useState<APIKey[]>([])
  const [loadingKeys, setLoadingKeys] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [selectedScopes, setSelectedScopes] = useState<string[]>([])
  const [expirationDays, setExpirationDays] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)

  // New key display
  const [newKey, setNewKey] = useState<APIKeyCreated | null>(null)
  const [copied, setCopied] = useState(false)

  const loadApps = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.getApps(client)
      setApps(response.apps)
      // Auto-select first app if available
      if (response.apps.length > 0 && !selectedAppSlug) {
        setSelectedAppSlug(response.apps[0].app.slug)
      }
    } catch (err) {
      console.error('Failed to load apps:', err)
      setError('Failed to load connected apps')
    } finally {
      setLoading(false)
    }
  }

  const loadKeysForApp = async (appSlug: string) => {
    if (!appSlug) return
    setLoadingKeys(true)
    try {
      const [appDetail, keysResponse] = await Promise.all([
        api.getApp(client, appSlug),
        api.getAPIKeys(client, appSlug),
      ])
      setSelectedAppDetail(appDetail.app)
      setKeys(keysResponse.keys)
    } catch (err) {
      console.error('Failed to load keys:', err)
      setError('Failed to load API keys for this app')
      setKeys([])
      setSelectedAppDetail(null)
    } finally {
      setLoadingKeys(false)
    }
  }

  useEffect(() => {
    loadApps()
  }, [])

  useEffect(() => {
    if (selectedAppSlug) {
      loadKeysForApp(selectedAppSlug)
    } else {
      setKeys([])
      setSelectedAppDetail(null)
    }
  }, [selectedAppSlug])

  const handleCreateKey = async () => {
    if (!selectedAppSlug || !newKeyName) return
    setCreating(true)
    setError(null)
    try {
      const keyData: { name: string; scopes: string[]; expires_in_days?: number } = {
        name: newKeyName,
        scopes: selectedScopes,
      }
      if (expirationDays) {
        keyData.expires_in_days = expirationDays
      }
      const created = await api.createAPIKey(client, selectedAppSlug, keyData)
      setNewKey(created)
      setKeys((prev) => [created, ...prev])
      // Don't close dialog yet - show the key
    } catch (err) {
      console.error('Failed to create key:', err)
      setError('Failed to create API key')
    } finally {
      setCreating(false)
    }
  }

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false)
    setNewKeyName('')
    setSelectedScopes([])
    setExpirationDays(null)
    setNewKey(null)
    setCopied(false)
  }

  const handleRevokeKey = async (keyId: string) => {
    if (!selectedAppSlug) return
    setActionLoading(keyId)
    try {
      await api.revokeAPIKey(client, selectedAppSlug, keyId)
      setKeys((prev) => prev.filter((k) => k.id !== keyId))
    } catch (err) {
      console.error('Failed to revoke key:', err)
      setError('Failed to revoke API key')
    } finally {
      setActionLoading(null)
    }
  }

  const copyKey = async () => {
    if (newKey?.raw_key) {
      await navigator.clipboard.writeText(newKey.raw_key)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope)
        ? prev.filter((s) => s !== scope)
        : [...prev, scope]
    )
  }

  if (loading) {
    return (
      <div>
        <PageHeader
          title="API Keys"
          description="Personal access tokens for automation"
        />
        <div className="space-y-4">
          <Skeleton className="h-10 w-48" />
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="py-6">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error && apps.length === 0) {
    return (
      <div>
        <PageHeader
          title="API Keys"
          description="Personal access tokens for automation"
        />
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-[var(--text-secondary)] mb-4">{error}</p>
            <Button onClick={loadApps}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (apps.length === 0) {
    return (
      <div>
        <PageHeader
          title="API Keys"
          description="Personal access tokens for automation"
        />
        <EmptyState
          icon={Key}
          title="No apps connected"
          description="Connect an app first to create API keys for it"
        />
      </div>
    )
  }

  const availableScopes = selectedAppDetail?.scopes || []

  return (
    <div>
      <PageHeader
        title="API Keys"
        description="Personal access tokens for automation"
        action={
          <Button onClick={() => setCreateDialogOpen(true)} disabled={!selectedAppSlug}>
            <Plus className="mr-2 h-4 w-4" />
            Create API key
          </Button>
        }
      />

      {error && (
        <Alert variant="error" className="mb-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* App selector */}
      <div className="mb-6">
        <Label className="mb-2 block">Select App</Label>
        <Select value={selectedAppSlug} onValueChange={setSelectedAppSlug}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Select an app" />
          </SelectTrigger>
          <SelectContent>
            {apps.map((app) => (
              <SelectItem key={app.app.slug} value={app.app.slug}>
                {app.app.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Keys list */}
      {loadingKeys ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="py-6">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : keys.length === 0 ? (
        <EmptyState
          icon={Key}
          title="No API keys yet"
          description={`Create an API key for ${apps.find(a => a.app.slug === selectedAppSlug)?.app.name || 'this app'}`}
          action={
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create API key
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          {keys.map((key) => (
            <Card key={key.id}>
              <CardContent className="py-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-medium text-[var(--text-primary)]">
                        {key.name}
                      </h3>
                      <Badge
                        variant={key.status === 'active' ? 'success' : key.status === 'expired' ? 'default' : 'error'}
                      >
                        {key.status}
                      </Badge>
                    </div>
                    <p className="text-sm font-mono text-[var(--text-secondary)] mt-1">
                      {key.key_prefix}...
                    </p>
                    <div className="mt-3 space-y-1">
                      {key.scopes.length > 0 && (
                        <p className="text-sm text-[var(--text-secondary)]">
                          Scopes: {key.scopes.join(', ')}
                        </p>
                      )}
                      <p className="text-sm text-[var(--text-muted)]">
                        Created: {formatDate(key.created_at)}
                        {key.expires_at && ` • Expires: ${formatDate(key.expires_at)}`}
                      </p>
                      <p className="text-sm text-[var(--text-muted)]">
                        Last used: {key.last_used_at ? formatTimeAgo(key.last_used_at) : 'Never'}
                        {key.use_count > 0 && ` • ${key.use_count} requests`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Revoke"
                          loading={actionLoading === key.id}
                          disabled={key.status === 'revoked'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Revoke API Key</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to revoke "{key.name}"? This
                            action cannot be undone. Any applications using this
                            key will lose access immediately.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleRevokeKey(key.id)}>
                            Revoke key
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create API Key Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => !open && handleCloseCreateDialog()}>
        <DialogContent>
          {newKey ? (
            // Show the newly created key
            <>
              <DialogHeader>
                <DialogTitle>API Key Created</DialogTitle>
                <DialogDescription>
                  Copy your API key now. You won't be able to see it again!
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="flex items-center gap-2 p-3 rounded-lg bg-[var(--bg-elevated-2)]">
                  <code className="flex-1 text-sm font-mono break-all">
                    {newKey.raw_key}
                  </code>
                  <Button variant="ghost" size="sm" onClick={copyKey}>
                    {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <div className="flex gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-500">
                      Make sure to save this key somewhere safe. Once you close this dialog, you won't be able to see it again.
                    </p>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCloseCreateDialog}>Done</Button>
              </DialogFooter>
            </>
          ) : (
            // Create form
            <>
              <DialogHeader>
                <DialogTitle>Create API Key</DialogTitle>
                <DialogDescription>
                  Create a new API key for {apps.find(a => a.app.slug === selectedAppSlug)?.app.name}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <Input
                  label="Name"
                  placeholder="production-deploy"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  hint="A name to identify this key"
                />

                {availableScopes.length > 0 && (
                  <div className="space-y-2">
                    <Label>Scopes (optional)</Label>
                    <div className="space-y-2 p-3 rounded-lg border border-[var(--border-default)] max-h-48 overflow-y-auto">
                      {availableScopes.map((scope) => (
                        <div key={scope.name} className="flex items-start gap-2">
                          <Checkbox
                            id={scope.name}
                            checked={selectedScopes.includes(scope.name)}
                            onCheckedChange={() => toggleScope(scope.name)}
                          />
                          <div className="flex-1">
                            <Label htmlFor={scope.name} className="font-mono text-sm">
                              {scope.name}
                            </Label>
                            <p className="text-xs text-[var(--text-muted)]">
                              {scope.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-[var(--text-muted)]">
                      Leave empty to use your account's default permissions
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Expiration</Label>
                  <Select
                    value={expirationDays?.toString() || 'never'}
                    onValueChange={(v) => setExpirationDays(v === 'never' ? null : parseInt(v))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="never">Never</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                      <SelectItem value="365">1 year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="secondary" onClick={handleCloseCreateDialog}>
                  Cancel
                </Button>
                <Button onClick={handleCreateKey} loading={creating} disabled={!newKeyName}>
                  Create API key
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  })
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins} min ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  return date.toLocaleDateString()
}
